// useListViewItemCoverState.js

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

const LIST_VIEW_COVER_LOAD_ROOT_MARGIN = '200px';

/**
 * useListViewItemCoverState
 *
 * Resolves and tracks the list item cover image state.
 * Cover URLs are resolved only after the cover enters the viewport.
 *
 * @param {object} params - Hook parameters.
 * @param {string} params.coverUrl - Optional cover URL or storage path.
 * @param {object} params.node - Node being rendered.
 *
 * @returns {object} Derived cover display state, image handlers, and cover container ref.
 */
const useListViewItemCoverState = ({
  coverUrl = '',
  node,
}) => {
  const coverContainerRef = useRef(null);
  const safeCoverUrl = typeof coverUrl === 'string' ? coverUrl.trim() : '';

  const [shouldLoadCover, setShouldLoadCover] = useState(false);
  const [resolvedCoverUrl, setResolvedCoverUrl] = useState(null);
  const [isCoverLoaded, setIsCoverLoaded] = useState(false);
  const [hasCoverError, setHasCoverError] = useState(false);

  useLayoutEffect(() => {
    setShouldLoadCover(false);
    setResolvedCoverUrl(null);
    setIsCoverLoaded(false);
    setHasCoverError(false);
  }, [node?.coverStoragePath, node?.key, safeCoverUrl]);

  useEffect(() => {
    const nodeEl = coverContainerRef.current;
    if (!safeCoverUrl || !nodeEl) {
      return undefined;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setShouldLoadCover(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target !== nodeEl || !entry.isIntersecting) return;
          setShouldLoadCover(true);
        });
      },
      {
        root: null,
        rootMargin: LIST_VIEW_COVER_LOAD_ROOT_MARGIN,
        threshold: 0,
      },
    );

    observer.observe(nodeEl);

    return () => observer.disconnect();
  }, [safeCoverUrl, node?.key]);

  useEffect(() => {
    let cancelled = false;

    if (!shouldLoadCover || !safeCoverUrl) {
      return undefined;
    }

    if (
      safeCoverUrl.startsWith('blob:')
      || safeCoverUrl.startsWith('data:')
      || safeCoverUrl.includes('://')
    ) {
      setResolvedCoverUrl(safeCoverUrl);
      return undefined;
    }

    (async () => {
      try {
        const signed = await resolveImageUrl({
          nodeKey: node?.key || null,
          storagePath: node?.coverStoragePath || null,
          url: safeCoverUrl,
        });

        if (!cancelled) {
          setResolvedCoverUrl(signed || null);
          setHasCoverError(!signed);
        }
      } catch {
        if (!cancelled) {
          setResolvedCoverUrl(null);
          setHasCoverError(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [shouldLoadCover, node?.coverStoragePath, node?.key, safeCoverUrl]);

  const displayCoverUrl = resolvedCoverUrl || '';
  const showCover = Boolean(safeCoverUrl) && !hasCoverError;
  const showCoverShimmer = showCover
    && shouldLoadCover
    && (!displayCoverUrl || !isCoverLoaded);

  /**
   * handleCoverError
   *
   * Marks the current cover image as failed.
   */
  const handleCoverError = () => {
    setHasCoverError(true);
    setIsCoverLoaded(false);
  };

  /**
   * handleCoverLoad
   *
   * Marks the current cover image as loaded.
   */
  const handleCoverLoad = () => {
    setIsCoverLoaded(true);
  };

  return useMemo(() => ({
    coverContainerRef,
    displayCoverUrl,
    handleCoverError,
    handleCoverLoad,
    showCover,
    showCoverShimmer,
  }), [displayCoverUrl, showCover, showCoverShimmer]);
};

export default useListViewItemCoverState;
